import { LoadingButton } from "@mui/lab"
import {
  Button,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  Dialog,
} from "@mui/material"
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { DialogTransition } from "../../services/utilities/utility"
import DialogTopBar from "../global/dialogTopBar"
import { useNavigate } from "react-router-dom"
import { MainContext } from "../../controllers/main"
import { ChallengeType, ChallengeVersionType } from "../../services/config/enum"
import { ChallengesContext } from "../../controllers/challenges"
import slugify from "react-slugify"
import ISODatePicker from "../global/isoDatePicker"
import ISOTimePicker from "../global/isoTimePicker"
import { mobilityMetrics } from "../../services/config/constants"
import Challenge from "../../models/challenge"

const DuplicateChallengeDialog = ({
  dialogOpen,
  setDialogOpen,
  handleDialogChange,
  challengeToDuplicate,
}: {
  dialogOpen: boolean
  setDialogOpen: Dispatch<SetStateAction<boolean>>
  handleDialogChange: MouseEventHandler<HTMLButtonElement>
  challengeToDuplicate: Challenge
}) => {
  const { setAnimation } = useContext(MainContext)
  const {
    setLoading,
    createChallengeParent,
    createDuplicatedChallengeDocument,
  } = useContext(ChallengesContext)
  const navigate = useNavigate()

  // prevent tab close
  const handleTabClose = useCallback((event: any) => {
    event.preventDefault()
    console.log("beforeunload event triggered")
    return (event.returnValue = "Are you sure you want to exit?")
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      window.addEventListener("beforeunload", handleTabClose)
    } else {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [dialogOpen])

  // configuration
  const [name, setName] = useState<string>(challengeToDuplicate.name)
  const [targetAmount, setTargetAmount] = useState<number | null>(
    challengeToDuplicate.targetAmount
  )

  // duration
  const [startsAt, setStartsAt] = useState<string>("")
  const [endsAt, setEndsAt] = useState<string>("")
  const [startsAtError, setStartsAtError] = useState<boolean>(false)
  const [endsAtError, setEndsAtError] = useState<boolean>(false)

  // current slide and scrolling when changes
  const [currentSlide, setCurrentSlide] = useState<number>(0)

  useEffect(() => {
    setTimeout(() => {
      let slide = document.getElementById(currentSlide.toString())
      slide?.scrollIntoView({ behavior: "smooth" })
    }, 10)
  }, [currentSlide])

  // slides
  const slides = [
    {
      label: "Configuration",
      component: (
        <Stack spacing={2}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
            }}
            onBlur={() => {
              if (name) {
                setName((current) => current.trim())
              }
            }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Target"
            value={targetAmount ?? ""}
            onChange={(e) => {
              if (e.target.value.length) {
                if (
                  e.target.value.includes(".") ||
                  e.target.value.includes(",")
                ) {
                  setTargetAmount(parseFloat(e.target.value))
                } else {
                  setTargetAmount(parseInt(e.target.value))
                }
              } else {
                setTargetAmount(null)
              }
            }}
            error={
              targetAmount === null ||
              (targetAmount !== null && targetAmount < 1)
            }
            helperText={
              targetAmount === null ||
              (targetAmount !== null && targetAmount < 1)
                ? "Invalid value"
                : ""
            }
          />
          <Stack direction="row" spacing={2}>
            {dialogOpen && (
              <ISODatePicker
                label="Start date"
                date={startsAt}
                setDate={(newDate: string) => {
                  if (startsAt) {
                    setStartsAt(
                      newDate
                        ? newDate.split("T")[0] + "T" + startsAt.split("T")[1]
                        : newDate
                    )
                  } else {
                    setStartsAt(newDate)
                  }
                }}
                error={startsAt && startsAtError}
                hour={12}
              />
            )}
            <ISOTimePicker
              label="Start time"
              time={startsAt}
              setTime={(newDate: string) => {
                setStartsAt(newDate)
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            {dialogOpen && (
              <ISODatePicker
                label="End date"
                date={endsAt}
                setDate={(newDate: string) => {
                  if (endsAt) {
                    setEndsAt(
                      newDate
                        ? newDate.split("T")[0] + "T" + endsAt.split("T")[1]
                        : newDate
                    )
                  } else {
                    setEndsAt(newDate)
                  }
                }}
                minDate={startsAt}
                error={endsAt && endsAtError}
                hour={12}
              />
            )}
            <ISOTimePicker
              label="End time"
              time={endsAt}
              setTime={(newDate: string) => {
                setEndsAt(newDate)
              }}
            />
          </Stack>
        </Stack>
      ),
    },
  ]

  // reset dialog on close
  useEffect(() => {
    if (!dialogOpen) {
      setTimeout(() => {
        setName(challengeToDuplicate.name)
        setTargetAmount(challengeToDuplicate.targetAmount)
        setStartsAt("")
        setEndsAt("")
        setStartsAtError(false)
        setEndsAtError(false)
        setCurrentSlide(0)
      }, 100)
    }
  }, [dialogOpen])

  // discard dialog
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false)

  const handleDiscardDialogCancel = () => {
    setDiscardDialogOpen(false)
  }

  const handleDiscardDialogClose = (e: any) => {
    setDiscardDialogOpen(false)
    handleDialogChange(e)
  }

  // should show discard dialog
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false)

  useEffect(() => {
    if (name) {
      setShowDiscardDialog(true)
    } else {
      setShowDiscardDialog(false)
    }
  }, [name])

  // create challenge
  const createNewChallenge = async () => {
    setLoading(true)

    const input: {
      endsAt: string
      metricId: string
      name: string
      startsAt: string
      targetAmount: number
      leaderboardGroupType: "average" | "sum"
      leaderboardMinMembers: number
      type: ChallengeType
      expCurrency: string
      uid: string
      teamId?: string
      personalTargetAmount?: number
      personalTargetPoints?: number
      privacyPolicy?: string
      versionType?: ChallengeVersionType
      showUserFullNames: boolean
    } = {
      endsAt: endsAt,
      metricId: challengeToDuplicate.metric.id,
      name: name,
      startsAt: startsAt,
      targetAmount: targetAmount,
      leaderboardGroupType: challengeToDuplicate.leaderboardGroupType,
      leaderboardMinMembers: challengeToDuplicate.leaderboardMinMembers,
      type: challengeToDuplicate.type,
      expCurrency: challengeToDuplicate.expCurrency,
      uid: slugify(name),
      versionType: ChallengeVersionType.pointsLeaderboardChallenge,
      showUserFullNames: challengeToDuplicate.showUserFullNames,
    }

    if (challengeToDuplicate.team) {
      input.teamId = challengeToDuplicate.team.id
    }
    if (mobilityMetrics.includes(challengeToDuplicate.metric.id)) {
      input.personalTargetAmount = challengeToDuplicate.personalTargetAmount
      input.personalTargetPoints = challengeToDuplicate.personalTargetPoints
    }
    if (challengeToDuplicate.privacyPolicy) {
      input.privacyPolicy = challengeToDuplicate.privacyPolicy
    }

    const result = await createChallengeParent(input)

    if (typeof result !== "boolean") {
      const newChallenge = result

      const documentResult = await createDuplicatedChallengeDocument(
        challengeToDuplicate,
        newChallenge.id
      )

      if (typeof documentResult !== "boolean") {
        setDialogOpen(false)
        setAnimation(false)
        setTimeout(() => {
          setLoading(false)
          navigate("/challenges")
        }, 250)
      } else {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={
        showDiscardDialog
          ? () => {
              setDiscardDialogOpen(true)
            }
          : handleDialogChange
      }
      TransitionComponent={DialogTransition}
    >
      <DialogTopBar
        handleDialogChange={
          showDiscardDialog
            ? () => {
                setDiscardDialogOpen(true)
              }
            : handleDialogChange
        }
        topBarText="Duplicate Challenge"
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: "70%",
            minWidth: 345,
            maxWidth: 850,
            height: 500,
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            style={{ paddingTop: 16 }}
          >
            Duplicate Challenge
          </Typography>
          <div
            style={{
              minWidth: 345,
              maxWidth: 850,
              height: "auto",
            }}
          >
            {slides.map((slide, index) => (
              <div
                key={index}
                id={index.toString()}
                style={{ minWidth: "100%", scrollSnapAlign: "center" }}
              >
                <CardContent>{slide.component}</CardContent>
              </div>
            ))}
          </div>
          <CardActions
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: 16,
              width: "100%",
            }}
          >
            <div style={{ width: "50%" }}>
              <Button
                variant="outlined"
                onMouseDown={(e: any) => {
                  if (!slides[currentSlide - 1]) {
                    if (showDiscardDialog) {
                      setDiscardDialogOpen(true)
                    } else {
                      handleDialogChange(e)
                    }
                  } else {
                    setCurrentSlide(currentSlide - 1)
                  }
                }}
              >
                Close
              </Button>
            </div>
            <div
              style={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <LoadingButton
                variant="contained"
                disabled={
                  (currentSlide === 0 && !name) ||
                  (currentSlide === 0 && targetAmount === null) ||
                  (currentSlide === 0 &&
                    targetAmount !== null &&
                    targetAmount < 1) ||
                  (currentSlide === 0 && !startsAt) ||
                  (currentSlide === 0 && !endsAt) ||
                  (currentSlide === 0 && startsAtError) ||
                  (currentSlide === 0 && endsAtError)
                }
                onMouseDown={
                  !slides[currentSlide + 1]
                    ? createNewChallenge
                    : () => {
                        setCurrentSlide(currentSlide + 1)
                      }
                }
              >
                Duplicate
              </LoadingButton>
            </div>
          </CardActions>
        </Paper>
      </div>
      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to close this window?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All entered data will be lost
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDialogCancel}>Cancel</Button>
          <Button onClick={handleDiscardDialogClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default DuplicateChallengeDialog
